import { currentLocale } from '@/lib/locale';
import { getAvatarColor, getInitials } from '@/lib/avatar';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { EpicTypeBadge } from '@/components/performance/EpicTypeBadge';
import { StatusBadge } from '@/components/performance/StatusBadge';
import { ScoreLineChart } from '@/components/performance/ScoreLineChart';
import { WinclapLoader } from '@/components/ui/winclap-loader';

type Category = 'Effective' | 'Affective' | 'Perspective';

const SCORE_CATEGORIES = [
  { key: 'effective' as const, color: '#22c55e' },
  { key: 'affective' as const, color: '#8b5cf6' },
  { key: 'perspective' as const, color: '#f59e0b' },
];

function computeCategoryScores(
  dimensions: { id: string; score: number | null }[],
  epicDimensions: { id: string; category: Category }[] | undefined | null,
) {
  if (!epicDimensions?.length) return { effective: null, affective: null, perspective: null };
  const catMap = new Map(epicDimensions.map(d => [d.id, d.category]));
  const sums: Record<string, { total: number; count: number }> = {
    Effective: { total: 0, count: 0 },
    Affective: { total: 0, count: 0 },
    Perspective: { total: 0, count: 0 },
  };
  for (const dim of dimensions) {
    const cat = catMap.get(dim.id);
    if (cat && dim.score !== null && sums[cat]) {
      sums[cat].total += dim.score;
      sums[cat].count += 1;
    }
  }
  return {
    effective: sums.Effective.count > 0 ? sums.Effective.total / sums.Effective.count : null,
    affective: sums.Affective.count > 0 ? sums.Affective.total / sums.Affective.count : null,
    perspective: sums.Perspective.count > 0 ? sums.Perspective.total / sums.Perspective.count : null,
  };
}

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatScore(v: number | null) {
  return v !== null ? v.toFixed(1) : '—';
}


export function PerformanceClapperProfile() {
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const decodedEmail = email ? decodeURIComponent(email) : '';

  const { data: allFeedbacks, isLoading: loadingFeedbacks } = trpc.performance.feedback.listByEvaluator.useQuery(undefined);
  const { data: epics, isLoading: loadingEpics } = trpc.performance.epicGlobal.list.useQuery();
  const { data: reportsData } = trpc.performance.reports.list.useQuery();

  const profile = useMemo(() => {
    if (!allFeedbacks || !epics) return null;

    const epicMap = new Map((epics ?? []).map((e) => [e.uuid, e]));
    const report = (reportsData?.reports ?? []).find((r) => r.email === decodedEmail);

    const clapperFbs = (allFeedbacks ?? []).filter((fb) => fb.clapperEmail === decodedEmail);
    const first = clapperFbs[0];
    if (!first && !report) return null;

    const feedbackItems = clapperFbs.map((fb: any) => {
      const epic = epicMap.get(fb.epicGlobalId);
      const scores = computeCategoryScores(fb.dimensions ?? [], epic?.dimensions);
      return {
        uuid: fb.uuid,
        epicGlobalId: fb.epicGlobalId,
        epicType: (fb.epicType ?? 'seasonal') as 'seasonal' | 'performance' | 'feedback_session',
        date: fb.date,
        status: fb.status as 'in_progress' | 'completed',
        scores,
        summary: fb.summary ?? null,
        dimensions: fb.dimensions ?? [],
      };
    }).sort((a: any, b: any) => b.date.localeCompare(a.date));

    const completedFeedbacks = feedbackItems.filter((f: any) => f.status === 'completed');

    const avg = (key: 'effective' | 'affective' | 'perspective') => {
      const vals = completedFeedbacks.map((f: any) => f.scores[key]).filter((v: any): v is number => v !== null);
      return vals.length > 0 ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null;
    };

    const name = report?.name ?? first?.clapperName ?? '';
    const lastName = report?.lastName ?? first?.clapperLastName ?? '';

    return {
      email: decodedEmail,
      name,
      lastName,
      fullName: `${name} ${lastName}`.trim() || decodedEmail,
      impactLevel: report?.impactLevel ?? first?.impactLevel ?? '',
      area: report?.area ?? null,
      picture: report?.picture ?? null,
      feedbacks: feedbackItems,
      completedFeedbacks,
      avgScores: { effective: avg('effective'), affective: avg('affective'), perspective: avg('perspective') },
    };
  }, [allFeedbacks, epics, reportsData, decodedEmail]);

  const isLoading = loadingFeedbacks || loadingEpics;

  const handleFeedbackClick = (fb: { uuid: string; epicType: string }) => {
    if (fb.epicType === 'performance') navigate(`/performance/epic/${fb.uuid}/performance-review`);
    else if (fb.epicType === 'feedback_session') navigate(`/performance/epic/${fb.uuid}/feedback-result`);
    else navigate(`/performance/epic/${fb.uuid}/analysis`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.back')} onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <p className="text-center py-16 text-muted-foreground">{t('performance.clapper_profile.no_evaluations')}</p>
      </div>
    );
  }

  const chartData = [...profile.completedFeedbacks]
    .sort((a: any, b: any) => a.date.localeCompare(b.date))
    .map((fb: any) => ({ date: fb.date, ...fb.scores }));

  const avatarColor = getAvatarColor(profile.fullName);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.back')} onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold text-gray-700 shrink-0 overflow-hidden"
          style={{ backgroundColor: avatarColor }}
        >
          {profile.picture
            ? <img src={profile.picture} alt={profile.fullName} className="w-full h-full object-cover" />
            : getInitials(profile.fullName) || <User className="h-5 w-5" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{profile.fullName}</h1>
          <p className="text-sm text-muted-foreground">{profile.area ?? profile.impactLevel}</p>
        </div>
      </div>

      {/* Category score cards */}
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-3">{t('performance.clapper_profile.avg_score_by_category')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {SCORE_CATEGORIES.map(({ key, color }) => (
            <Card key={key}>
              <CardContent className="pt-5 pb-4 flex flex-col items-center gap-1">
                <span className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">{t(`performance.score_chart.${key}`)}</span>
                <span className="text-2xl font-bold" style={{ color }}>
                  {formatScore(profile.avgScores[key])}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="pt-5">
            <p className="text-2xl font-bold">{profile.feedbacks.length}</p>
            <p className="text-sm text-muted-foreground">{t('performance.clapper_profile.total_evaluations')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-2xl font-bold">{profile.completedFeedbacks.length}</p>
            <p className="text-sm text-muted-foreground">{t('performance.clapper_profile.completed')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Evolution line chart */}
      {chartData.length > 1 && (
        <Card>
          <CardContent className="pt-5">
            <ScoreLineChart data={chartData} />
          </CardContent>
        </Card>
      )}

      {/* Feedback list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('performance.clapper_profile.feedback_history')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {profile.feedbacks.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground text-sm">{t('performance.clapper_profile.no_feedbacks')}</p>
          ) : (
            <div className="divide-y">
              {profile.feedbacks.map((fb: any) => (
                <button
                  key={fb.uuid}
                  onClick={() => handleFeedbackClick(fb)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors text-left"
                >
                  <span className="text-xs font-medium text-muted-foreground whitespace-nowrap shrink-0">
                    {formatDate(fb.date)}
                  </span>

                  <EpicTypeBadge type={fb.epicType} />

                  {fb.status === 'in_progress' && (
                    <StatusBadge status={fb.status} />
                  )}

                  <div className="flex-1" />

                  {fb.epicType === 'feedback_session' && fb.summary ? (
                    <span className="text-muted-foreground text-xs italic truncate max-w-[140px] sm:max-w-[200px] hidden sm:block">
                      "{fb.summary}"
                    </span>
                  ) : (
                    <div className="hidden sm:flex items-center gap-3 sm:gap-4">
                      {SCORE_CATEGORIES.map(({ key, color }) => (
                        <div key={key} className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] text-muted-foreground uppercase">{key[0].toUpperCase()}</span>
                          <span className="text-sm font-bold" style={{ color }}>
                            {formatScore(fb.scores[key])}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0 ml-2" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
